import { isEmpty, find } from 'lodash-es'

// Libs
import {
  setHostData,
  setParticipantList,
  setAudience,
  updateHandUpStatus,
  setUserCount,
  setIsKickoff,
  setChathistory,
  getSilentUserList,
  setSilentUserList,
  removeSilentUserList,
} from '@/redux/reducers/socket'
import { getRoleByUid } from '@/methods/getRoleByUid'
import { setLiveType } from '@/redux/reducers/room'

// Constants
import { WS_EVENT_ROOM, WS_EVENT, WS_EVENT_ROOM_STATES, HAND_UP_STATUS } from '@/constants/socketEvent'
import { ROLE } from '@/constants/role'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { VALID_BRAND_NAME } from '@/constants/brand'

// Types
import { IMessage } from '../handleMessageReceive'

const room = {
  action({ message, userToken, delayTime = 0, brandName, silentUserList = [] }: IMessage) {
    if (message?.ssid === WS_EVENT_ROOM.state) {
      if (message.body?.key === WS_EVENT.HAND_UP) {
        return handleHandUpStatus({ message }) // 直播間是否開啟上麥功能
      } else if (message.body?.key === WS_EVENT_ROOM_STATES.participant) {
        return handleParticipantReceive({ message }) // 直播間人員列表
      } else if (message.body?.key === WS_EVENT_ROOM_STATES.userCount) {
        return handleUserCount({ message }) // 直播間人員列表
      } else if (message.body?.key === WS_EVENT_ROOM_STATES.chathistory) {
        return handleChathistory({ message, brandName, silentUserList })
      } else if (message.body?.key === WS_EVENT_ROOM_STATES.active) {
        if (message.body?.value === 1) {
          // 收到 { key: active, value: 1 } 代表直播開始
          //if (!isHost) {
          return setLiveType({ liveType: ROOM_LIVE_TYPE.CURRENT, delay: delayTime })
          //}
        } else if (message.body?.value === 0) {
          // 收到 { key: active, value: 0 } 代表直播結束
          return setLiveType({ liveType: ROOM_LIVE_TYPE.PAST, delay: delayTime })
        }
      } else if (message.body?.key === WS_EVENT_ROOM_STATES.silentUsers) {
        return handleSilentUsersUpdate({ silentUserList: message.body?.value, brandName })
      } else if (!message.body?.key) {
        // 如果沒回 key 就當做是處理 roomInfo
        return handleRoomSocketInfoUpdate({ message })
      }
    } else if (message?.ssid === WS_EVENT_ROOM.userState) {
      // canChat 應該要用 key 判斷，但現在回的 key 是 token
      // 一般來說 key 要是 kickoff 或 silentUsers
      // 所以只能先判斷 value
      if (message.body?.value?.canChat === 1) {
        return handleRemoveSilentUser({ silentUser: message.body?.key || '', brandName })
      } else if (message.body?.value?.canChat === -1) {
        return handleAddSilentUser({ silentUser: message.body?.key || '', brandName })
      } else if (message.body?.key === 'kickoff') {
        return handleRoomKickOff({ message, userToken })
      } else {
        return handleUserState({ message, userToken })
      }
    }
  },
}

export default room

const handleUserState = ({ message, userToken }: IMessage) => {
  const data = message.body

  const { key = '', value } = data

  if (userToken === key) return

  const role = getRoleByUid(key)

  const isHostMessage = role === ROLE.HOST
  const isAudience = role === ROLE.AUDIENCE

  // 這裡的 isHost 不是看會員的 role，而是看 message 的 key(token) 是不是 host
  if (isHostMessage) {
    return setHostData({ hostData: value })
  } else if (isAudience) {
    return setAudience({ audience: value })
  }
}

const handleUserCount = ({ message }: IMessage) => {
  const data = message.body

  return setUserCount({ userCount: data?.value })
}

const handleChathistory = ({ message, brandName, silentUserList = [] }: IMessage) => {
  const chathistory = message.body.value

  chathistory?.forEach((item: any) => {
    if (item.extra && typeof item.extra === 'string') item.extra = JSON.parse(item.extra)

    item.context = item.msg

    // 針對 PXEC 品牌直播間，標記被 ban 的用戶留言
    if (brandName === VALID_BRAND_NAME.PXEC && silentUserList.length > 0) {
      // 嘗試從多個可能的欄位取得 userToken
      const userToken = item.extra?.token || item.token || item.sender || item.from?.token
      if (userToken && silentUserList.includes(userToken)) {
        item.isHiddenMessage = true
      } else {
        item.isHiddenMessage = false
      }
    } else {
      item.isHiddenMessage = false
    }

    item.type = 'chat'
    item.isHost = item.extra?.isHost
    item.name = item.extra?.name
    // 保存 token 以便識別用戶
    if (!item.token) {
      item.token = item.extra?.token || item.sender
    }
  })

  chathistory.reverse()

  return setChathistory({ chathistory })
}

const handleHandUpStatus = ({ message }: IMessage) => {
  const data = message.body

  const canHandUp = data.value === HAND_UP_STATUS.ON

  return updateHandUpStatus({ canHandUp })
}

const handleParticipantReceive = ({ message }: IMessage) => {
  const actionArray = []

  const data: any = message.body
  const { value } = data

  const hostData = find(value, { role: ROLE.HOST })

  actionArray.push(setParticipantList({ participantList: value }))

  if (!isEmpty(hostData)) actionArray.push(setHostData({ hostData }))

  return actionArray
}

const handleRoomSocketInfoUpdate = ({ message }: IMessage) => {
  const data = message.body

  const { canHandUp } = data

  return updateHandUpStatus({ canHandUp: canHandUp === HAND_UP_STATUS.ON })
}

const handleRoomKickOff = ({ message, userToken }: IMessage) => {
  if (message.body.value === userToken) {
    return setIsKickoff({ isKickoff: true })
  }
}

// 處理接收完整的被 ban 用戶列表
const handleSilentUsersUpdate = ({ silentUserList, brandName }: { silentUserList: string[]; brandName?: string }) => {
  // 判斷是否需要靜音（只針對 PXEC 品牌）
  const shouldMute = brandName === VALID_BRAND_NAME.PXEC && silentUserList.length > 0

  // 在單一 action 中同時更新 silentUserList 和將被 ban 用戶的訊息改成提示文字
  return getSilentUserList({ silentUserList, shouldMute })
}

// 處理新增被 ban 用戶
const handleAddSilentUser = ({ silentUser, brandName }: { silentUser: string; brandName?: string }) => {
  // 判斷是否需要靜音（只針對 PXEC 品牌）
  const shouldMute = brandName === VALID_BRAND_NAME.PXEC

  // 在單一 action 中同時新增到 silentUserList 和將該用戶的訊息改成提示文字
  return setSilentUserList({ silentUser, shouldMute })
}

// 處理移除被 ban 用戶
const handleRemoveSilentUser = ({ silentUser, brandName }: { silentUser: string; brandName?: string }) => {
  // 判斷是否需要恢復訊息（只針對 PXEC 品牌）
  const shouldUnmute = brandName === VALID_BRAND_NAME.PXEC

  // 在單一 action 中同時移除 silentUser 和恢復該用戶的原始訊息
  return removeSilentUserList({ silentUser, shouldUnmute })
}
